import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  X,
  Check,
  Loader2,
  Eye,
  UserPlus,
  AlertCircle,
} from 'lucide-react';
import { showToast } from '../../context/ToastContext';
import { createUserService, getUsersService } from '../../service/user';
import type { IUser } from '@tool-no-hu/shared';
import { CustomDropdown } from '../../components/CustomDropdown';

interface CreateUserData {
  username: string;
  password: string;
  note: string;
}

const emptyForm: CreateUserData = {
  username: '',
  password: '',
  note: '',
};

const PAGE_SIZE = 10;

export const AdminAccounts: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<CreateUserData>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const isFirstRender = useRef(true);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getUsersService({ page, limit: PAGE_SIZE, search });
      setUsers((res?.items as IUser[]) || []);
      setTotalPages(res?.totalPages || 1);
      setTotalCount(res?.totalCount || 0);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      showToast.error('Không thể tải danh sách người dùng');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounce search
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      fetchUsers();
    }, 400);
    return () => clearTimeout(timer);
  }, [search, fetchUsers]);

  const handleOpenModal = () => {
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setForm(emptyForm);
  };

  const handleFormChange = (field: keyof CreateUserData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      showToast.error('Vui lòng điền đầy đủ các trường bắt buộc');
      return;
    }
    setSubmitting(true);
    try {
      await createUserService({
        username: form.username,
        password: form.password,
        note: form.note,
      });
      showToast.success('Tạo tài khoản mới thành công');
      setIsModalOpen(false);
      setForm(emptyForm);
      fetchUsers();
    } catch (err: any) {
      const message =
        err?.response?.data?.message || 'Tạo tài khoản thất bại';
      showToast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetail = (id: string) => {
    navigate(`/admin/accounts/${id}`);
  };

  return (
    <div className="space-y-4 text-[#e2e8f0]">
      {/* Page Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3.5">
          <div className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#2d4a8a] bg-[#1e2a4a]">
            <Users size={20} className="text-[#3b82f6]" />
          </div>
          <div>
            <h1 className="m-0 text-[18px] font-bold text-[#e2e8f0]">
              Quản lý tài khoản
            </h1>
            <p className="m-0 mt-0.5 text-[13px] text-[#64748b]">
              Danh sách người dùng có role &quot;user&quot;
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleOpenModal}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#3b82f6] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#2563eb]"
        >
          <UserPlus size={14} />
          Thêm tài khoản
        </button>
      </div>

      {/* Search */}
      <div className="rounded-xl border border-[#252a38] bg-[#161920] px-4 py-3">
        <div className="relative max-w-md">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm theo tên người dùng..."
            className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] py-2 pl-9 pr-3 text-[13px] text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="overflow-hidden rounded-xl border border-[#252a38] bg-[#161920]">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-[#10131a]">
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                  Tên người dùng
                </th>
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                  Note
                </th>
                <th className="px-4 py-2.5 text-center text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                  Trạng thái
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-[12px] font-medium text-[#64748b]">
                      <Loader2 size={14} className="animate-spin text-[#3b82f6]" />
                      Đang tải...
                    </div>
                  </td>
                </tr>
              ) : users.length > 0 ? (
                users.map((u) => {
                  const inactive = u.status === 'INACTIVE';
                  return (
                    <tr
                      key={u._id}
                      className="border-t border-[#1e2235] transition-colors hover:bg-[#1c2030]"
                    >
                      <td className="px-4 py-3 text-[13px] font-semibold text-[#e2e8f0]">
                        {u.username}
                      </td>
                      <td className="px-4 py-3 text-[12px] text-[#94a3b8]">
                        {u.note || '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${
                            inactive
                              ? 'border-[#7f1d1d] bg-[#1c0a0a] text-[#ef4444]'
                              : 'border-[#14532d] bg-[#0d1f14] text-[#22c55e]'
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              inactive ? 'bg-[#ef4444]' : 'bg-[#22c55e]'
                            }`}
                          />
                          {inactive ? 'Đã khoá' : 'Hoạt động'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleViewDetail(u._id as string)}
                          className="inline-flex items-center gap-1 rounded-lg border border-[#252a38] bg-[#10131a] px-3 py-1.5 text-[11px] font-bold text-[#7dd3fc] transition-colors hover:border-[#3b82f6] hover:bg-[#1e2a4a] hover:text-[#3b82f6]"
                        >
                          <Eye size={12} />
                          Xem chi tiết
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-2 text-[#334155]">
                      <AlertCircle size={32} className="text-[#252a38]" />
                      <p className="text-[13px] font-semibold text-[#475569]">
                        Không tìm thấy người dùng nào
                      </p>
                      <span className="text-[11px] text-[#334155]">
                        Thử thay đổi từ khoá tìm kiếm hoặc tạo tài khoản mới
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && users.length > 0 && (
          <div className="flex items-center justify-between border-t border-[#252a38] bg-[#10131a] px-4 py-3 text-[12px]">
            <span className="text-[#64748b]">Tổng: {totalCount} người dùng</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-[#252a38] bg-[#161920] px-3 py-1.5 text-[11px] font-bold text-[#e2e8f0] transition-colors hover:border-[#334155] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Trước
              </button>
              <span className="font-mono text-[12px] font-semibold text-[#94a3b8]">
                Trang {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-[#252a38] bg-[#161920] px-3 py-1.5 text-[11px] font-bold text-[#e2e8f0] transition-colors hover:border-[#334155] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Sau
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d0f14]/80 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-[#252a38] bg-[#161920] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)]">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-[#252a38] px-6 py-4">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#2d4a8a] bg-[#1e2a4a]">
                  <UserPlus size={14} className="text-[#3b82f6]" />
                </div>
                <h2 className="m-0 text-[15px] font-bold text-[#e2e8f0]">
                  Thêm tài khoản mới
                </h2>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="rounded-md p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0]"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
              {/* Username */}
              <div>
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748b]">
                  Tên người dùng <span className="text-[#ef4444]">*</span>
                </label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => handleFormChange('username', e.target.value)}
                  placeholder="Nhập tên đăng nhập"
                  className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
                />
              </div>

              {/* Password */}
              <div>
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748b]">
                  Mật khẩu <span className="text-[#ef4444]">*</span>
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                  placeholder="Nhập mật khẩu"
                  className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
                />
              </div>

              {/* Note */}
              <div>
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748b]">
                  Ghi chú
                </label>
                <textarea
                  value={form.note}
                  onChange={(e) => handleFormChange('note', e.target.value)}
                  placeholder="Ghi chú thêm về tài khoản..."
                  rows={2}
                  className="w-full resize-none rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
                />
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-[#252a38] pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-lg border border-[#252a38] bg-transparent px-4 py-2.5 text-[13px] font-bold text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#e2e8f0]"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-1.5 rounded-lg bg-[#3b82f6] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:bg-[#1e2235] disabled:text-[#334155]"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Đang tạo...
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      Tạo tài khoản
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccounts;